import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LexicalEditor } from 'lexical'
import { useRef } from 'react'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
import {
    convertLexicalToDBJson,
    setJsonScriptEditor,
} from '@/features/screenplay/components/editor/utils/JsonUtils'
import { ParaNode } from './editor/nodes/ParaNode'
import { StEditorInstancePlugin } from './editor/plugins/StEditorInstancePlugin'
import StEnterPlugin from './editor/plugins/StEnterPlugin'
import StNodeToolbarPlugin from './editor/plugins/StNodeToolbarPlugin'
import { useStEditorProvider } from './StEditorProvider'
import StAIWriterChatPlugin from './editor/plugins/StAIWriterChatPlugin'
import StParaTransformPlugin from './editor/plugins/StParaTransformPlugin'
import { useUpdateStoryMutation } from '../queries/story.queries'
import { useStoryEditor } from '../hooks/useStoryEditor'
import { toastService } from '@/shared/services/toast.service'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { useStEditorStore } from '../stores/st-editor.store'

const StoryEditor = () => {
    const { jsonScript, storyEntity } = useStoryEditor()
    const { featuresEnabled } = useStEditorProvider()
    const { mutateAsync: updateStory } = useUpdateStoryMutation()
    const { setStoryEntity } = useStEditorStore()
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    function onError(error: any) {
        console.error(error)
    }

    const initialConfig = {
        namespace: 'StEditor',
        onError,
        nodes: [ParaNode],
        editorState: (initialEditor: LexicalEditor) => {
            if (!jsonScript) return
            setJsonScriptEditor(initialEditor, jsonScript)
        },
    }

    if (!storyEntity || !jsonScript) return null
    return (
        <LexicalComposer key={storyEntity.id} initialConfig={initialConfig}>
            <StEditorInstancePlugin />
            <RichTextPlugin
                contentEditable={
                    <ContentEditable
                        className="flex flex-col outline-none items-center w-[8in] min-h-screen p-[1in] bg-gray-50"
                        id={KEYS.ST_CONTENT_EDITABLE}
                    />
                }
                ErrorBoundary={LexicalErrorBoundary}
            />
            <StEnterPlugin />
            <OnChangePlugin
                onChange={(editorState) => {
                    const id = storyEntity?.id
                    const editorJson = editorState.toJSON()
                    const data = convertLexicalToDBJson(editorJson)

                    if (!id || !data) return

                    const nextStoryEntity = { ...storyEntity, content: data, updated_at: new Date() }

                    // Keep sidebar actions in sync with the editor immediately.
                    setStoryEntity(nextStoryEntity)

                    if (saveTimeoutRef.current) {
                        clearTimeout(saveTimeoutRef.current)
                    }

                    saveTimeoutRef.current = setTimeout(async () => {
                        try {
                            await updateStory(nextStoryEntity)
                        } catch (err) {
                            console.error('failed to save content:', err)
                            toastService.error(err as string)
                        }
                    }, 1000)
                }}
            />
            <HistoryPlugin />
            {featuresEnabled.nodeToolbar && <StNodeToolbarPlugin />}
            <StParaTransformPlugin />
            <StAIWriterChatPlugin />
        </LexicalComposer>
    )
}

export default StoryEditor
